import * as Sentry from "@sentry/react";
import { onCLS, onINP, onFCP, onLCP, onTTFB } from "web-vitals";
import type { Metric } from "web-vitals";

/**
 * Initialize Sentry for error tracking
 */
export const initSentry = () => {
  if (import.meta.env.MODE === "production" && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      environment: import.meta.env.MODE,
      beforeSend(event, hint) {
        // Filter out non-critical errors
        if (event.exception) {
          const error = hint.originalException;
          if (error instanceof Error) {
            // Filter out common browser extension errors
            if (error.message.includes("Extension context")) {
              return null;
            }
          }
        }
        return event;
      },
    });
  }
};

/**
 * Report web vitals to monitoring service
 */
const reportWebVitals = (metric: Metric) => {
  if (import.meta.env.MODE === "production") {
    // Send to analytics service
    console.info("Web Vital:", metric);

    // Send to Sentry
    Sentry.metrics.distribution(metric.name, metric.value, {
      unit: "millisecond",
    });
  }
};

/**
 * Initialize web vitals monitoring
 */
export const initWebVitals = () => {
  onCLS(reportWebVitals);
  onINP(reportWebVitals);
  onFCP(reportWebVitals);
  onLCP(reportWebVitals);
  onTTFB(reportWebVitals);
};

/**
 * Track custom events
 */
export const trackEvent = (eventName: string, properties?: Record<string, unknown>) => {
  if (import.meta.env.MODE === "production") {
    // Send to analytics service
    console.info("Event:", eventName, properties);

    // Add breadcrumb to Sentry
    Sentry.addBreadcrumb({
      category: "user-action",
      message: eventName,
      data: properties,
      level: "info",
    });
  }
};

/**
 * Set user context for error tracking
 */
export const setUserContext = (userId: string, userData?: Record<string, unknown>) => {
  Sentry.setUser({
    id: userId,
    ...userData,
  });
};

/**
 * Clear user context on logout
 */
export const clearUserContext = () => {
  Sentry.setUser(null);
};
